import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserRoles,
  getBranchUserRoles,
  getUserRole,
  createUser,
  updateUserRole,
  updateUser,
  deleteUserRole
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch all user roles (org admin)
export function useUserRoles() {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userRoles', organization?.id],
    queryFn: () => getUserRoles(organization.id),
    enabled: isInitialized && !!organization?.id
  });

  return {
    userRoles: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch branch user roles (branch admin)
export function useBranchUserRoles(branchId) {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branchUserRoles', organization?.id, branchId],
    queryFn: () => getBranchUserRoles(organization.id, branchId),
    enabled: isInitialized && !!organization?.id && !!branchId
  });

  return {
    userRoles: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single user role
export function useUserRole(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userRole', id],
    queryFn: () => getUserRole(id),
    enabled: !!id
  });

  return {
    userRole: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create user
export function useCreateUser() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async userData => {
      const result = await createUser({
        ...userData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // Wait for queries to be invalidated and refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['branchUserRoles'] })
      ]);
      toast.success('User created successfully');
    },
    onError: error => {
      console.error('Create user error:', error);
      toast.error(error?.message || 'Failed to create user');
    }
  });

  return {
    createUser: mutation.mutate,
    createUserAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update user role
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateUserRole(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // Wait for queries to be invalidated and refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['branchUserRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['userRole'] })
      ]);
      toast.success('User role updated successfully');
    },
    onError: error => {
      console.error('Update user role error:', error);
      toast.error('Failed to update user role');
    }
  });

  return {
    updateUserRole: mutation.mutate,
    updateUserRoleAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update user info
export function useUpdateUser() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateUser(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // Wait for queries to be invalidated and refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['branchUserRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['userRole'] })
      ]);
      toast.success('User updated successfully');
    },
    onError: error => {
      console.error('Update user error:', error);
      toast.error('Failed to update user');
    }
  });

  return {
    updateUser: mutation.mutate,
    updateUserAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete user role
export function useDeleteUserRole() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteUserRole(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      // Wait for queries to be invalidated and refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['userRoles'] }),
        queryClient.invalidateQueries({ queryKey: ['branchUserRoles'] })
      ]);
      toast.success('User deleted successfully');
    },
    onError: error => {
      console.error('Delete user role error:', error);
      toast.error('Failed to delete user');
    }
  });

  return {
    deleteUserRole: mutation.mutate,
    deleteUserRoleAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}
